import {
    ASSISTANT_AUDIO_MAX_SIZE,
    ASSISTANT_AUDIO_MIME_TO_EXTENSION,
} from '@/consts/assistant'

// Kept apart from the assistant's own failures: a denied microphone is the
// browser saying no, not the assistant being down.
export const RECORDER_ERRORS = {
    UNSUPPORTED: 'unsupported',
    INSECURE_CONTEXT: 'insecure_context',
    PERMISSION_DENIED: 'permission_denied',
    NO_DEVICE: 'no_device',
    TOO_LARGE: 'too_large',
    EMPTY: 'empty',
    UNKNOWN: 'unknown',
}

export class RecorderError extends Error {
    constructor(reason, cause) {
        super(reason)
        this.name = 'RecorderError'
        this.reason = reason
        this.cause = cause
    }
}

// In preference order: Chrome and Firefox take the first, Safari and iOS the
// third. All four are formats the backend accepts.
const CANDIDATE_MIME_TYPES = [
    'audio/webm',
    'audio/ogg',
    'audio/mp4',
    'audio/wav',
]

const isSupported = () =>
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    !!navigator?.mediaDevices?.getUserMedia

// Over plain http the microphone is not denied, it is absent — so "allow the
// mic" would be advice nobody can act on.
const isSecureContext = () =>
    typeof window !== 'undefined' &&
    (window.isSecureContext || window.location?.hostname === 'localhost')

const pickMimeType = () =>
    CANDIDATE_MIME_TYPES.find((type) =>
        window.MediaRecorder.isTypeSupported(type)
    ) || ''

const extensionFor = (mimeType) => {
    // MediaRecorder reports `audio/webm;codecs=opus`.
    const base = (mimeType || '').split(';')[0].trim().toLowerCase()

    return ASSISTANT_AUDIO_MIME_TO_EXTENSION[base] || 'webm'
}

/**
 * Live levels off the same stream the recorder is using.
 *
 * Frequency rather than waveform, because that is what a voice recorder reads
 * as. Only the bottom of the spectrum is sampled: speech energy sits there, and
 * spreading the bars across the full range leaves most of them flat while the
 * first few do all the moving.
 *
 * Optional by design — a browser without Web Audio still records, it just does
 * not draw.
 */
const createLevelMeter = (stream) => {
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext

    if (!AudioContextCtor) return null

    try {
        const context = new AudioContextCtor()
        const analyser = context.createAnalyser()

        analyser.fftSize = 256
        // Without smoothing the bars flicker per frame instead of moving.
        analyser.smoothingTimeConstant = 0.75

        context.createMediaStreamSource(stream).connect(analyser)
        context.resume?.()

        const bins = new Uint8Array(analyser.frequencyBinCount)
        const voiceBins = Math.floor(bins.length * 0.45)

        return {
            read: (count) => {
                analyser.getByteFrequencyData(bins)

                const step = voiceBins / count
                const levels = []

                for (let i = 0; i < count; i += 1) {
                    const from = Math.floor(i * step)
                    const to = Math.max(from + 1, Math.floor((i + 1) * step))
                    let sum = 0

                    for (let j = from; j < to; j += 1) sum += bins[j]

                    levels.push(sum / (to - from) / 255)
                }

                return levels
            },
            close: () => context.close?.().catch(() => {}),
        }
    } catch (error) {
        return null
    }
}

const requestStream = async () => {
    try {
        return await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (error) {
        if (
            error?.name === 'NotAllowedError' ||
            error?.name === 'SecurityError'
        ) {
            throw new RecorderError(RECORDER_ERRORS.PERMISSION_DENIED, error)
        }

        if (
            error?.name === 'NotFoundError' ||
            error?.name === 'DevicesNotFoundError'
        ) {
            throw new RecorderError(RECORDER_ERRORS.NO_DEVICE, error)
        }

        throw new RecorderError(RECORDER_ERRORS.UNKNOWN, error)
    }
}

/**
 * One dictated turn: start, then stop and get a file.
 *
 * @returns {Promise<{stop: () => Promise<File>, cancel: () => void, mimeType: string}>}
 */
export const startRecording = async () => {
    if (!isSecureContext()) {
        throw new RecorderError(RECORDER_ERRORS.INSECURE_CONTEXT)
    }

    if (!isSupported()) {
        throw new RecorderError(RECORDER_ERRORS.UNSUPPORTED)
    }

    const stream = await requestStream()
    const mimeType = pickMimeType()
    const recorder = new window.MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
    )
    const chunks = []

    recorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size > 0) chunks.push(event.data)
    })

    const meter = createLevelMeter(stream)

    // The microphone indicator stays lit until every track is stopped, and the
    // audio context outlives the stream unless it is closed with it.
    const releaseStream = () => {
        stream.getTracks().forEach((t) => t.stop())
        meter?.close()
    }

    recorder.start()

    return {
        mimeType: recorder.mimeType || mimeType,
        meter,

        stop: () =>
            new Promise((resolve, reject) => {
                recorder.addEventListener('stop', () => {
                    releaseStream()

                    // The recorder may not have honoured the type we asked for.
                    const type = recorder.mimeType || mimeType || 'audio/webm'
                    const blob = new Blob(chunks, { type })

                    if (blob.size === 0) {
                        reject(new RecorderError(RECORDER_ERRORS.EMPTY))
                        return
                    }

                    if (blob.size > ASSISTANT_AUDIO_MAX_SIZE) {
                        reject(new RecorderError(RECORDER_ERRORS.TOO_LARGE))
                        return
                    }

                    resolve(
                        new File([blob], `turn.${extensionFor(type)}`, { type })
                    )
                })

                recorder.addEventListener('error', (event) => {
                    releaseStream()
                    reject(
                        new RecorderError(RECORDER_ERRORS.UNKNOWN, event?.error)
                    )
                })

                if (recorder.state === 'inactive') {
                    releaseStream()
                    reject(new RecorderError(RECORDER_ERRORS.EMPTY))
                    return
                }

                recorder.stop()
            }),

        cancel: () => {
            if (recorder.state !== 'inactive') recorder.stop()
            releaseStream()
        },
    }
}

export default { startRecording, RECORDER_ERRORS }
